import React from 'react';
import { motion } from 'framer-motion';
import { LuPalette as Palette, LuTrendingUp as TrendingUp, LuTarget as Target, LuCode as Code } from 'react-icons/lu';
import { containerVariants, cardVariants } from '../../lib/animations';

const iconMap = { Palette, TrendingUp, Target, Code };

const teamCapabilities = [
  {
    title: 'Design Experts',
    description: 'Senior UI/UX designers with 8+ years experience in startup environments',
    icon: 'Palette'
  },
  {
    title: 'CRO Specialists',
    description: 'Data-driven optimization experts who understand conversion psychology',
    icon: 'TrendingUp'
  },
  {
    title: 'Product Strategists',
    description: 'Strategic thinkers who align design with business goals and user needs',
    icon: 'Target'
  },
  {
    title: 'Development Partners',
    description: 'Technical implementation experts for flawless design-to-code execution',
    icon: 'Code'
  }
];

const Team = () => {
  return (
    <section className="py-16 md:py-24 bg-neutral-950">
      <div className="container mx-auto px-4 md:px-6">
        <motion.div
          className="text-center mb-12 md:mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-3 md:mb-4">
            Meet Your Dedicated Expert Team
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-neutral-400">
            A multidisciplinary team of experts ready to embed with your startup
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6 md:gap-8 max-w-5xl mx-auto mb-12 md:mb-16">
          <motion.div
            className="flex-shrink-0 w-full group relative cursor-pointer"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            whileHover={{ y: -8 }}
          >
            <div className="relative h-[500px] sm:h-[550px] md:h-[605px] rounded-3xl overflow-hidden bg-neutral-900 border border-white/10">
              <div className="absolute inset-0">
                <img
                  src="https://cdn.prod.website-files.com/62714322a659c56310ea38fe/6281f2cccf36021cc98322e6_1Z9A1746.jpg"
                  alt="Rohan Mishra"
                  className="w-full h-full object-cover grayscale group-hover:scale-105 transition-transform duration-700 ease-out"
                />
              </div>

              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent opacity-80" />
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 z-20">
                <div className="inline-flex items-center px-3 py-1 bg-white/10 backdrop-blur-md rounded-lg border border-white/10 mb-3 md:mb-4">
                  <span className="text-xs font-bold text-white uppercase tracking-wider">Founder & Creative Director</span>
                </div>

                <h3 className="text-2xl sm:text-2xl md:text-3xl font-bold text-white mb-2 leading-tight">
                  Rohan Mishra
                </h3>

                <div className="grid grid-rows-[0fr] group-hover:grid-rows-[1fr] transition-[grid-template-rows] duration-500 ease-out">
                  <div className="overflow-hidden">
                    <p className="text-neutral-300 text-sm leading-relaxed pt-3 pb-1 border-t border-white/20 mt-3 opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-100">
                      10+ years as content strategist, founder of Mastry.in (UI/UX education platform), and design educator. Worked with 200+ Indian startups across E-commerce, Fintech, and AI.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="flex-shrink-0 w-full group relative cursor-pointer"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            whileHover={{ y: -8 }}
          >
            <div className="relative h-[500px] sm:h-[550px] md:h-[605px] rounded-3xl overflow-hidden bg-neutral-900 border border-white/10">
              <div className="absolute inset-0">
                <img
                  src="https://cdn.prod.website-files.com/62714322a659c56310ea38fe/628398f3dfdba3b016290323_1Z9A1858.jpg"
                  alt="Rakshita Gupta"
                  className="w-full h-full object-cover grayscale group-hover:scale-105 transition-transform duration-700 ease-out"
                />
              </div>

              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent opacity-80" />
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 z-20">
                <div className="inline-flex items-center px-3 py-1 bg-white/10 backdrop-blur-md rounded-lg border border-white/10 mb-3 md:mb-4">
                  <span className="text-xs font-bold text-white uppercase tracking-wider">Creative Director</span>
                </div>

                <h3 className="text-2xl sm:text-2xl md:text-3xl font-bold text-white mb-2 leading-tight">
                  Rakshita Gupta
                </h3>

                <div className="grid grid-rows-[0fr] group-hover:grid-rows-[1fr] transition-[grid-template-rows] duration-500 ease-out">
                  <div className="overflow-hidden">
                    <p className="text-neutral-300 text-sm leading-relaxed pt-3 pb-1 border-t border-white/20 mt-3 opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-100">
                      Expert UI/UX designer specializing in conversion-focused design for startups. 8+ years experience creating data-driven design solutions that drive measurable business results.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        <motion.div
          className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {teamCapabilities.map((capability, index) => {
            const IconComponent = iconMap[capability.icon];
            return (
              <motion.div
                key={index}
                className="text-center p-6 md:p-8 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors backdrop-blur-sm"
                variants={cardVariants}
                whileHover={{ y: -8 }}
              >
                <motion.div
                  className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-4 md:mb-6 backdrop-blur-sm"
                  whileInView={{ rotate: 360 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <IconComponent className="w-7 h-7 md:w-8 md:h-8 text-white" />
                </motion.div>
                <h4 className="text-lg md:text-xl font-bold text-white mb-2 md:mb-3">
                  {capability.title}
                </h4>
                <p className="text-sm md:text-base text-neutral-400">
                  {capability.description}
                </p>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default Team;